// Per-demo-project tours, all instances of ONE parameterized factory — the
// "extend the tutorial for a specific project" pattern in action. Each demo
// project (generated by the generate-customer-demo skill) is a real governed
// pipeline: a ~150-row dataset uploaded as a file source, elements per column,
// mappings, default rules, a postgres target and a structured_load pipeline
// that ran — plus a bespoke dashboard app that reads the target LIVE.
//
// Adding a tour for a new demo = one makeDemoTour() call in DEMO_TOURS below.

function esc(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function makeDemoTour({ slug, company, domain, table, view, rows = 150 }) {
  const c = esc(company);
  return {
    key: `demo:${slug}`,
    title: `${company} demo`,
    description: `How this ${company} demo works end-to-end: the ${domain} dataset, its governance recipe, the pipeline that ran, and the live dashboard.`,
    steps: [
      {
        title: `A real pipeline, built for ${company}`,
        body:
          `This project is a live Cortex demo prepared for <b>${c}</b>, around ${esc(domain)} data — ` +
          `<b>not a mock-up</b>. A realistic ~${rows}-row dataset was ingested, governed and loaded ` +
          `into a database by an actual pipeline run, and the dashboard you'll see at the end reads ` +
          `that database <b>live</b>. This tour follows the data through the system.`,
      },
      {
        page: "data-sources",
        target: '[data-tour="page-data-sources"], main',
        title: "The dataset arrived as a source",
        body:
          `The <code>${esc(table)}</code> dataset was uploaded as a file <b>source</b>. Connecting it ` +
          `captured the schema and the analysis profiled every column — types, fill rates, samples.`,
      },
      {
        page: "data-elements",
        target: '[data-tour="page-data-elements"], main',
        title: "Every column became a governed element",
        body:
          `Each dataset column is declared as a <b>data element</b> — typed, categorized, with a ` +
          `primary key marked. This is ${c}'s canonical vocabulary; everything downstream refers to ` +
          `these elements, not to raw column names.`,
      },
      {
        page: "source-mappings",
        target: '[data-tour="page-source-mappings"], main',
        title: "Mappings wire the file to the elements",
        body:
          `Every source column is <b>mapped</b> to its element. The mapping is what makes the source ` +
          `usable by the pipeline — and if ${c} later swapped the file for a database, only the ` +
          `mappings would change, not the recipe around them.`,
      },
      {
        page: "processing-rules",
        target: '[data-tour="page-processing-rules"], main',
        title: "Rules validate every record",
        body:
          `Validation/cleanup <b>processing rules</b> are attached to the mapped elements and run on ` +
          `every record during the pipeline — trimming, number normalization, type checks. Rules are ` +
          `config, not code: add one and the next run applies it to the whole dataset.`,
      },
      {
        page: "pipeline",
        target: '[data-tour="page-pipeline"], main',
        title: "The pipeline that actually ran",
        body:
          `The graph reads the file source, applies mappings + rules, and loads a <b>Postgres target</b> ` +
          `(a <code>structured_load</code> transform). It has genuinely run — the run history below ` +
          `shows rows written. Press <b>Run</b> again after any recipe change and the target refreshes.`,
      },
      {
        page: `apps/${view}`,
        target: '[data-tour="app-embed"], main',
        waitMs: 15000,
        title: `${company}'s live dashboard`,
        body:
          `This app is published from the project and styled for ${c}. Crucially, it contains ` +
          `<b>zero embedded data</b>: every number and row is fetched at load time from the governed ` +
          `Postgres target through an engine endpoint. Change a rule, re-run the pipeline — the ` +
          `dashboard follows.`,
      },
      {
        title: "What this demo shows",
        body:
          `<b>Source → elements → mappings → rules → pipeline → governed target → live app</b>, all ` +
          `versioned in this project and all reproducible for ${c}'s real systems: swap the sample ` +
          `file for their databases and the same recipe governs production data.<br/><br/>Replay ` +
          `this tour any time via <b>Tutorial</b> in the sidebar — or take “What is Cortex?” for the ` +
          `full product walkthrough.`,
      },
    ],
  };
}

// slug → [tours]. View names are the fork's *.view endpoints (see the
// generate-customer-demo skill; they are stable per demo).
export const DEMO_TOURS = {
  demo_4results_v2: [makeDemoTour({
    slug: "demo_4results_v2", company: "4Results", domain: "improvement-project portfolio",
    table: "improvement_project_portfolio", view: "cortex.improvements.dashboard.view",
  })],
  demo_itintegro_v2: [makeDemoTour({
    slug: "demo_itintegro_v2", company: "IT.integro", domain: "Business Central deployment",
    table: "bc_deployment_portfolio", view: "cortex.bc_deployments.dashboard.view",
  })],
  demo_supremis_v2: [makeDemoTour({
    slug: "demo_supremis_v2", company: "SUPREMIS", domain: "SAP B1 implementation portfolio",
    table: "sap_b1_implementation_portfolio", view: "cortex.sap_implementations.dashboard.view",
  })],
  demo_tas_force_v2: [makeDemoTour({
    slug: "demo_tas_force_v2", company: "T.A.S. FORCE", domain: "engagement portfolio",
    table: "engagement_portfolio", view: "cortex.engagements.dashboard.view",
  })],
  demo_berlin_heart: [makeDemoTour({
    slug: "demo_berlin_heart", company: "Berlin Heart", domain: "VAD implant registry",
    table: "vad_implant_registry", view: "cortex.vad.dashboard.view", rows: 12,
  })],
  demo_techmatik: [makeDemoTour({
    slug: "demo_techmatik", company: "Techmatik", domain: "plan-vs-actual order margin watch",
    table: "order_margin_watch", view: "cortex.ordermargin.dashboard.view",
  })],
  demo_budokrusz: [makeDemoTour({
    slug: "demo_budokrusz", company: "Budokrusz", domain: "multi-site batch & fleet control",
    table: "site_batch_deliveries", view: "cortex.sitebatch.dashboard.view",
  })],
  demo_miranda: [makeDemoTour({
    slug: "demo_miranda", company: "Miranda", domain: "batch traceability across 4 production stages",
    table: "batch_traceability", view: "cortex.traceability.dashboard.view",
  })],
};
